import { batch, computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import { isEqual } from 'es-toolkit';
import { getUnderlyingSymbolFromInstrument, parseInstrument } from 'src/lib/instrumentUtils';
import { LinkingMailboxModel } from 'src/models/LinkingMailboxModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { SelfOwnedWidget, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { WidgetContainerChannelSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerChannelSlice';
import { WidgetContainerTabSlice } from 'src/services/WorkspaceService/WidgetContainer/slices/WidgetContainerTabSlice';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';

// The three widgets have the same inbound product policy. Legacy matrices remain
// only for delivering our outbound events to widgets that have not migrated.
const instrumentSources = new Set<WidgetType>([
    'results',
    'watchlist',
    'order-blotter',
    'positions',
    'executions',
    'orders',
    'locates',
    'lab',
    'alpha-lens',
    'alpha-eye',
    'searchalpha',
    'scanner',
    'screener',
    'news',
    'events',
    'filings',
    'tradingview-chart',
]);

export interface TimeListWorkspaceInit {
    mode: 'workspace';
    tabId: WidgetId;
    widgetContainer: WidgetContainerController;
    workspaceService: Pick<WorkspaceService, 'publishLinkIntent' | 'subscribeLinkEvents' | 'handleAddWidgetAction'>;
    logger: Logger;
}

export type TimeListWidgetHost =
    | TimeListWorkspaceInit
    | {
          mode: 'standalone';
          openChartForSymbol: (symbol: string) => void;
      };

export interface TimeListWidgetInit<T> {
    state: T;
    host: TimeListWidgetHost;
}

/** State owned by the widget model in both workspace and standalone routes. */
export class TimeListWidgetState<T extends { symbolFilter: string | null }, TPersisted>
    implements SelfOwnedWidget<TPersisted>, Disposable
{
    readonly #stack = new DisposableStack();
    readonly #state: Signal<T>;
    readonly #clean: Signal<T>;
    readonly channelSlice?: WidgetContainerChannelSlice;
    readonly widgetTabSlice?: WidgetContainerTabSlice;
    readonly dirty: ReadonlySignal<boolean>;
    readonly settings: ReadonlySignal<T>;

    constructor(
        readonly host: TimeListWidgetHost,
        initialState: T,
        readonly serialize: (state: T) => TPersisted,
    ) {
        this.#state = signal(initialState);
        this.#clean = signal(initialState);
        this.settings = computed(() => this.#state.value);
        this.dirty = computed(() => !isEqual(this.serialize(this.#state.value), this.serialize(this.#clean.value)));
        if (host.mode === 'standalone') return;
        const init = host;
        const channelSlice = (this.channelSlice = this.#stack.use(
            new WidgetContainerChannelSlice({
                container: init.widgetContainer,
                tabId: init.tabId,
            }),
        ));
        this.widgetTabSlice = this.#stack.use(
            new WidgetContainerTabSlice({
                container: init.widgetContainer,
                tabId: init.tabId,
                logger: init.logger,
            }),
        );
        const mailbox = this.#stack.use(
            new LinkingMailboxModel({
                workspaceService: init.workspaceService,
                acceptsLinkEvent: (event) =>
                    channelSlice.channel.value !== null &&
                    event.channelId === channelSlice.channel.value &&
                    event.type === 'instrument-selected' &&
                    (event.from.kind === 'external' ||
                        (event.from.tabId !== init.tabId && instrumentSources.has(event.from.widgetType))),
            }),
        );
        this.#stack.defer(() => mailbox.dispose());
        this.#stack.defer(
            effect(() => {
                const linked = mailbox.instrument.value;
                if (linked) untracked(() => this.setSymbolFromLink(linked.value));
            }),
        );
    }

    peek(): T {
        return this.#state.peek();
    }
    update(partial: Partial<T>): void {
        if (this.#stack.disposed) return;
        const next = { ...this.#state.peek(), ...partial };
        if (isEqual(next, this.#state.peek())) return;
        this.#state.value = next;
    }
    /** Resolve creation defaults without marking hydration as a user edit. */
    initialize(partial: Partial<T>): void {
        if (this.#stack.disposed) return;
        batch(() => {
            this.#clean.value = { ...this.#clean.peek(), ...partial };
            this.update(partial);
        });
    }
    setSymbolFromLink(instrument: string): void {
        this.update({
            ...this.peek(),
            symbolFilter: getUnderlyingSymbolFromInstrument(parseInstrument(instrument)),
        });
    }
    onSelectSymbol(symbol: string): void {
        if (!symbol || this.#stack.disposed || this.host.mode === 'standalone') return;
        const host = this.host;
        void host.workspaceService
            .publishLinkIntent({
                type: 'instrument-selected',
                fromTabId: host.tabId,
                details: { instrument: symbol },
            })
            .catch((error: unknown) => host.logger.error('Failed to publish time-list symbol selection', { error }));
    }
    openChartForSymbol(symbol: string): void {
        if (this.#stack.disposed) return;
        if (this.host.mode === 'standalone') {
            this.host.openChartForSymbol(symbol);
            return;
        }
        const host = this.host;
        void host.workspaceService
            .handleAddWidgetAction({
                type: 'addWidgetToDashboard',
                widget: {
                    creationType: 'new-tradingview-chart',
                    defaultSymbol: symbol,
                    channelId: this.channelSlice?.channel.value ?? null,
                },
            })
            .catch((error: unknown) => host.logger.error('Failed to open chart', { error }));
    }
    openLensForSymbol(symbol: string): void {
        if (this.#stack.disposed || this.host.mode === 'standalone') return;
        const host = this.host;
        void host.workspaceService
            .handleAddWidgetAction({
                type: 'addWidgetToDashboard',
                widget: {
                    creationType: 'new-alpha-eye',
                    defaultSymbol: symbol,
                    channelId: this.channelSlice?.channel.value ?? null,
                },
            })
            .catch((error: unknown) => host.logger.error('Failed to open AlphaEye', { error }));
    }
    toJSON(): TPersisted {
        return this.serialize(this.#state.peek());
    }
    markClean(): void {
        this.#clean.value = this.#state.peek();
    }
    [Symbol.dispose](): void {
        this.#stack.dispose();
    }
}
